import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { useAuth } from "../context/AuthContext";
import { DashboardLayout } from "../components/DashboardLayout";
import { API } from "../lib/constants";
import { MetricsGrid } from "../components/affiliate/MetricsGrid";
import { WithdrawForm } from "../components/affiliate/WithdrawForm";
import { ReferralsTable } from "../components/affiliate/ReferralsTable";
import { CommissionsTable } from "../components/affiliate/CommissionsTable";
import { AnalyticsPanel } from "../components/affiliate/AnalyticsPanel";

function SocialShareButtons({ getReferralLink }) {
  const shareOnTwitter = () => {
    const text = "I'm using QAIRO AI to create amazing content 10x faster! Try it free with my link:";
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(getReferralLink("twitter"))}`, '_blank');
  };
  const shareOnLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(getReferralLink("linkedin"))}`, '_blank');
  };
  const shareOnFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getReferralLink("facebook"))}`, '_blank');
  };
  const shareOnWhatsApp = () => {
    const text = `Hey! I've been using QAIRO AI to create amazing content 10x faster. Try it free: ${getReferralLink("whatsapp")}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };
  const shareOnTelegram = () => {
    const text = "I'm using QAIRO AI to create amazing content 10x faster! Try it free:";
    window.open(`https://t.me/share/url?url=${encodeURIComponent(getReferralLink("telegram"))}&text=${encodeURIComponent(text)}`, '_blank');
  };
  const shareOnReddit = () => {
    const title = "QAIRO AI - Create blog posts, ads & images with AI";
    window.open(`https://reddit.com/submit?url=${encodeURIComponent(getReferralLink("reddit"))}&title=${encodeURIComponent(title)}`, '_blank');
  };
  const shareViaEmail = () => {
    const subject = "Check out QAIRO AI - Create amazing content with AI";
    const body = `Hey!\n\nI've been using QAIRO AI to create blog posts, ads, and marketing images with AI. It's amazing!\n\nYou can try it free here: ${getReferralLink("email")}\n\nLet me know what you think!`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const buttons = [
    { fn: shareOnTwitter, id: "twitter", label: "Twitter", path: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" },
    { fn: shareOnLinkedIn, id: "linkedin", label: "LinkedIn", path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" },
    { fn: shareOnFacebook, id: "facebook", label: "Facebook", path: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" },
    { fn: shareOnWhatsApp, id: "whatsapp", label: "WhatsApp", path: "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" },
    { fn: shareOnTelegram, id: "telegram", label: "Telegram", path: "M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" },
    { fn: shareOnReddit, id: "reddit", label: "Reddit", path: "M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" },
    { fn: shareViaEmail, id: "email", label: "Email", path: null }
  ];

  return (
    <div className="flex flex-wrap gap-3">
      {buttons.map((btn) => (
        <button key={btn.id} onClick={btn.fn} className="flex items-center gap-2 bg-white/20 text-white px-4 py-2 font-medium hover:bg-white/30 transition-colors rounded-lg" data-testid={`share-${btn.id}`}>
          {btn.path ? (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d={btn.path}/></svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
          )}
          {btn.label}
        </button>
      ))}
    </div>
  );
}

function ReferralCard({ stats, user, copyReferralLink, copied, getReferralLink }) {
  return (
    <div className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white p-8 mb-8 rounded-2xl shadow-xl" data-testid="referral-card">
      <div className="flex items-center gap-4 mb-4">
        <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center">
          <span className="text-3xl font-black text-[#0066FF]">Q</span>
        </div>
        <div>
          <h2 className="text-xl font-bold">Your Referral Link</h2>
          <p className="text-white/70 text-sm">Share and earn 20% commission forever</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <input type="text" value={`${window.location.origin}/register?ref=${stats?.affiliate_code || user?.affiliate_code || ''}`} readOnly className="flex-1 bg-white/10 border border-white/30 p-3 text-white rounded-xl" data-testid="referral-link" />
        <button onClick={copyReferralLink} className="bg-white text-[#0066FF] px-6 py-3 font-bold hover:bg-gray-100 transition-colors rounded-xl" data-testid="copy-referral-btn">
          {copied ? "Copied!" : "Copy Link"}
        </button>
      </div>
      <div className="mt-6 pt-4 border-t border-white/20">
        <p className="text-sm text-white/70 mb-3">Share on social media:</p>
        <SocialShareButtons getReferralLink={getReferralLink} />
      </div>
    </div>
  );
}

function HowItWorks() {
  return (
    <div className="mt-8 bg-[#F4F4F5] border border-black p-6">
      <h3 className="text-lg font-bold mb-4">How It Works</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div><div className="text-3xl font-black text-[#0066FF]/30 mb-2">01</div><h4 className="font-bold mb-1">Share Your Link</h4><p className="text-sm text-gray-600">Share your unique referral link with friends, on social media, or your website</p></div>
        <div><div className="text-3xl font-black text-[#0066FF]/30 mb-2">02</div><h4 className="font-bold mb-1">They Sign Up & Subscribe</h4><p className="text-sm text-gray-600">When someone signs up and purchases a subscription using your link</p></div>
        <div><div className="text-3xl font-black text-[#0066FF]/30 mb-2">03</div><h4 className="font-bold mb-1">You Earn 20% Forever</h4><p className="text-sm text-gray-600">Earn 20% of every payment they make - recurring for life!</p></div>
      </div>
    </div>
  );
}

function OverviewTab({ stats, user, commissions, analytics, showAnalytics, setShowAnalytics, showWithdraw, setShowWithdraw, loadAffiliateData, getReferralLink, copyReferralLink, copied }) {
  return (
    <>
      <ReferralCard stats={stats} user={user} copyReferralLink={copyReferralLink} copied={copied} getReferralLink={getReferralLink} />
      <MetricsGrid stats={stats} />

      {(stats?.pending_earnings || 0) >= 50 && (
        <div className="mb-8">
          <button onClick={() => setShowWithdraw(!showWithdraw)} className="bg-green-600 text-white px-6 py-3 font-bold hover:bg-green-700 transition-colors rounded-xl" data-testid="withdraw-btn">
            Request Withdrawal (${stats?.pending_earnings?.toFixed(2)})
          </button>
        </div>
      )}
      {showWithdraw && (
        <WithdrawForm stats={stats} onSuccess={() => { setShowWithdraw(false); loadAffiliateData(); }} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <ReferralsTable referrals={stats?.recent_referrals} />
        <CommissionsTable commissions={commissions} />
      </div>

      <AnalyticsPanel analytics={analytics} showAnalytics={showAnalytics} setShowAnalytics={setShowAnalytics} />
      <HowItWorks />
    </>
  );
}

function LeaderboardTab({ leaderboard, stats }) {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white p-8 rounded-2xl text-center relative overflow-hidden">
        <div className="absolute top-4 left-4 w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center">
          <span className="text-3xl font-black text-white/80">Q</span>
        </div>
        <h2 className="text-2xl font-bold mb-2">Top Affiliate Earners</h2>
        <p className="text-white/80">See how you stack up against other QAIRO AI affiliates</p>
      </div>
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left text-xs uppercase tracking-wider font-bold text-gray-500">Rank</th>
              <th className="px-6 py-4 text-left text-xs uppercase tracking-wider font-bold text-gray-500">Affiliate</th>
              <th className="px-6 py-4 text-right text-xs uppercase tracking-wider font-bold text-gray-500">Total Earned</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {leaderboard.length > 0 ? leaderboard.map((affiliate) => (
              <tr key={affiliate.rank} className={affiliate.rank <= 3 ? "bg-yellow-50/50" : ""} data-testid={`leaderboard-${affiliate.rank}`}>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ${
                    affiliate.rank === 1 ? "bg-yellow-400 text-yellow-900" :
                    affiliate.rank === 2 ? "bg-gray-300 text-gray-700" :
                    affiliate.rank === 3 ? "bg-orange-300 text-orange-900" :
                    "bg-gray-100 text-gray-600"
                  }`}>{affiliate.rank}</span>
                </td>
                <td className="px-6 py-4 font-medium">{affiliate.display_name}</td>
                <td className="px-6 py-4 text-right"><span className="text-green-600 font-bold">${affiliate.total_earned.toFixed(2)}</span></td>
              </tr>
            )) : (
              <tr><td colSpan="3" className="px-6 py-12 text-center text-gray-500">No affiliates on the leaderboard yet. Be the first!</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="bg-[#F4F4F5] border border-black p-6 rounded-xl text-center">
        <p className="text-gray-600">Your current earnings: <span className="font-bold text-[#0066FF]">${stats?.total_earned?.toFixed(2) || "0.00"}</span></p>
        <p className="text-sm text-gray-500 mt-2">Keep sharing your referral link to climb the leaderboard!</p>
      </div>
    </div>
  );
}

function AssetsTab({ marketingAssets, copyToClipboard, copied }) {
  const downloadSvg = (svg, filename) => {
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white p-8 rounded-2xl relative overflow-hidden">
        <div className="absolute top-4 right-4 w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center">
          <span className="text-3xl font-black text-white/80">Q</span>
        </div>
        <h2 className="text-2xl font-bold mb-2">Marketing Assets</h2>
        <p className="text-white/80">Professional banners and copy templates to promote QAIRO AI</p>
      </div>

      <div>
        <h2 className="text-xl font-bold mb-4">Downloadable Banners</h2>
        <p className="text-gray-600 mb-6">Download these QAIRO AI banners for your website, blog, or social media.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {marketingAssets.banners?.map((banner) => (
            <div key={banner.size} className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm" data-testid={`banner-${banner.size}`}>
              <div className="bg-gray-50 rounded-xl overflow-hidden mb-4 flex items-center justify-center p-4" style={{ minHeight: "150px" }}>
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(banner.svg, { USE_PROFILES: { svg: true } }) }} style={{ maxWidth: "100%", maxHeight: "200px" }} />
              </div>
              <div className="flex items-center justify-between">
                <div><p className="font-medium">{banner.name}</p><p className="text-sm text-gray-500">{banner.size}</p></div>
                <button onClick={() => downloadSvg(banner.svg, `qairo-banner-${banner.size}.svg`)} className="bg-[#0066FF] text-white px-4 py-2 rounded-xl font-medium hover:bg-[#0052CC] transition-colors" data-testid={`download-banner-${banner.size}`}>Download SVG</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-xl font-bold mb-4">Ready-to-Use Copy</h2>
        <p className="text-gray-600 mb-6">Copy and paste these QAIRO AI promotional templates.</p>
        <div className="space-y-4">
          {marketingAssets.copy_templates?.map((template) => (
            <div key={template.type} className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm" data-testid={`copy-template-${template.type.toLowerCase()}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="px-3 py-1 bg-[#0066FF]/10 text-[#0066FF] text-sm font-medium rounded-lg">{template.type}</span>
                <button onClick={() => copyToClipboard(template.text)} className="text-[#0066FF] text-sm font-medium hover:underline">{copied ? "Copied!" : "Copy"}</button>
              </div>
              <p className="text-gray-700 whitespace-pre-wrap text-sm">{template.text}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#0066FF] rounded-lg flex items-center justify-center"><span className="text-lg font-bold text-white">Q</span></div>
          <h3 className="font-bold">Your QAIRO AI Affiliate Links</h3>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between bg-white p-3 rounded-lg border">
            <span className="text-sm font-medium">Main Referral Link</span>
            <div className="flex items-center gap-2">
              <code className="text-xs bg-gray-100 px-2 py-1 rounded">{marketingAssets.referral_link}</code>
              <button onClick={() => copyToClipboard(marketingAssets.referral_link)} className="text-[#0066FF] text-sm font-medium">Copy</button>
            </div>
          </div>
          <div className="flex items-center justify-between bg-white p-3 rounded-lg border">
            <span className="text-sm font-medium">Affiliate Code</span>
            <div className="flex items-center gap-2">
              <code className="text-xs bg-gray-100 px-2 py-1 rounded font-bold">{marketingAssets.affiliate_code}</code>
              <button onClick={() => copyToClipboard(marketingAssets.affiliate_code)} className="text-[#0066FF] text-sm font-medium">Copy</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AffiliatePage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [copied, setCopied] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [marketingAssets, setMarketingAssets] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [withdrawSuccess, setWithdrawSuccess] = useState("");

  const loadAffiliateData = useCallback(async () => {
    try {
      const [statsRes, commissionsRes, analyticsRes, leaderboardRes, assetsRes] = await Promise.all([
        axios.get(`${API}/affiliate/stats`),
        axios.get(`${API}/affiliate/commissions`),
        axios.get(`${API}/affiliate/analytics`),
        axios.get(`${API}/affiliate/leaderboard`),
        axios.get(`${API}/affiliate/marketing-assets`)
      ]);
      setStats(statsRes.data);
      setCommissions(commissionsRes.data);
      setAnalytics(analyticsRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
      setMarketingAssets(assetsRes.data);
    } catch {
      // Failed to load
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAffiliateData();
  }, [loadAffiliateData]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).catch(() => {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${stats?.affiliate_code || user?.affiliate_code}`;
    copyToClipboard(link);
  };

  const getReferralLink = (source = "") => {
    const code = stats?.affiliate_code || user?.affiliate_code;
    const baseLink = `${window.location.origin}/register?ref=${code}`;
    return source ? `${baseLink}&src=${source}` : baseLink;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 md:p-8 flex items-center justify-center min-h-[400px]">
          <div className="animate-pulse text-gray-500">Loading affiliate data...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Affiliate Program</h1>
          <p className="text-gray-600">Earn 20% recurring commission on every referral</p>
        </div>

        {withdrawSuccess && (
          <div className="bg-green-50 border border-green-500 text-green-700 p-4 mb-6" data-testid="withdraw-success">{withdrawSuccess}</div>
        )}

        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {[
            { id: "overview", label: "Overview" },
            { id: "leaderboard", label: "Leaderboard" },
            { id: "assets", label: "Marketing Assets" }
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-6 py-3 font-medium transition-colors ${activeTab === tab.id ? 'border-b-2 border-[#0066FF] text-[#0066FF]' : 'text-gray-500 hover:text-gray-700'}`} data-testid={`tab-${tab.id}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "overview" && (
          <OverviewTab stats={stats} user={user} commissions={commissions} analytics={analytics} showAnalytics={showAnalytics} setShowAnalytics={setShowAnalytics} showWithdraw={showWithdraw} setShowWithdraw={setShowWithdraw} loadAffiliateData={loadAffiliateData} getReferralLink={getReferralLink} copyReferralLink={copyReferralLink} copied={copied} />
        )}
        {activeTab === "leaderboard" && <LeaderboardTab leaderboard={leaderboard} stats={stats} />}
        {activeTab === "assets" && marketingAssets && <AssetsTab marketingAssets={marketingAssets} copyToClipboard={copyToClipboard} copied={copied} />}
      </div>
    </DashboardLayout>
  );
}
